import Mention from '@tiptap/extension-mention'
import { ReactRenderer } from '@tiptap/react'
import { SuggestionOptions } from '@tiptap/suggestion'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import tippy, { GetReferenceClientRect } from 'tippy.js'
import { MessageVariables } from '../../../pages/Marketing/constants/MessageVariables'

interface VariableItem {
  id: string
  label: string
}

export const VariableList = forwardRef((props: any, ref: React.ForwardedRef<any>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items[index]

    if (item) {
      props.command({ id: `{{${item.id}}}` })
    }
  }

  useEffect(() => setSelectedIndex(0), [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
        const selectedEl = containerRef.current?.children[selectedIndex]
        if (selectedEl && 'offsetTop' in selectedEl)
          containerRef.current?.scrollTo({ top: Number(selectedEl?.offsetTop) })
        return true
      }

      if (event.key === 'ArrowDown') {
        setSelectedIndex((selectedIndex + 1) % props.items.length)
        const selectedEl = containerRef.current?.children[selectedIndex]
        if (selectedEl && 'offsetTop' in selectedEl)
          containerRef.current?.scrollTo({ top: Number(selectedEl?.offsetTop) })
        return true
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex)
        return true
      }

      return false
    },
  }))

  return (
    <div className="popover">
      <div
        ref={containerRef}
        className="popover-body p-0"
        style={{ maxHeight: '13rem', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {props?.items?.length ? (
          props?.items?.map((item: VariableItem, index: number) => (
            <button
              type="button"
              key={item.id}
              onClick={() => selectItem(index)}
              className={`dropdown-item ${selectedIndex === index ? 'active' : ''}`.trim()}
            >
              <code className="bg-transparent">
                {`{{`}
                {item.id}
                {`}}`}
              </code>
              <div className="text-body-secondary small">{item.label}</div>
            </button>
          ))
        ) : (
          <div className="dropdown-item">No result</div>
        )}
      </div>
    </div>
  )
})

VariableList.displayName = 'VariableList'

export const suggestion: Omit<SuggestionOptions<VariableItem>, 'editor'> = {
  char: '{{',
  items: ({ query }) => {
    return MessageVariables.filter(({ id, label }) =>
      query
        .split('_')
        .every(
          (q) =>
            id.split('_').some((i) => i.toLowerCase().includes(q.toLowerCase())) ||
            label.split(' ').some((i) => i.toLowerCase().includes(q.toLowerCase())),
        ),
    )
  },

  render: () => {
    let component: ReactRenderer<any>
    let popup: InstanceType<any> | null = null

    return {
      onStart: (props) => {
        component = new ReactRenderer(VariableList, {
          props,
          editor: props.editor,
        })

        if (!props.clientRect) {
          return
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        })
      },

      onUpdate(props: any) {
        component.updateProps(props)

        if (!props.clientRect) {
          return
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
        })
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup?.[0].hide()

          return true
        }

        return component.ref?.onKeyDown(props)
      },

      onExit() {
        popup?.[0].destroy()
        component.destroy()
      },
    }
  },
}

export const Variable = Mention.extend({
  name: 'variable',
  parseHTML() {
    return [
      {
        tag: 'span[data-type="variable"]',
      },
    ]
  },
}).configure({
  suggestion,
  renderLabel({ node }) {
    return `${node.attrs.label ?? node.attrs.id}`
  },
})
